import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { db } from '../firebase';
import { ALWAYS_VISIBLE } from '../config/permissions';

const AUTH_STORAGE_KEY = 'noor_admin_user';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem(AUTH_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error('Failed to load admin user from localStorage:', e);
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const logout = useCallback(() => {
    setUser(null);
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch (e) {
      console.error('Failed to remove admin user from localStorage:', e);
    }
  }, []);

  // Background verification & permission sync
  useEffect(() => {
    if (!user?.id) return;

    let mounted = true;
    db.collection('admins')
      .doc(user.id)
      .get()
      .then((doc) => {
        if (!mounted) return;
        if (doc.exists) {
          const data = doc.data();
          const storedPermissions = Array.isArray(data.permissions) ? data.permissions : [];
          const permissions = Array.from(new Set([...ALWAYS_VISIBLE, ...storedPermissions]));
          const updatedUser = {
            id: doc.id,
            email: data.email,
            name: data.name,
            phone: data.phone || '',
            role: data.role || 'editor',
            permissions,
          };
          setUser(updatedUser);
          try {
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(updatedUser));
          } catch (e) {
            console.error('Failed to update admin user in localStorage:', e);
          }
        } else {
          // If the admin document was removed from Firestore, log them out
          logout();
        }
      })
      .catch((err) => {
        console.error('Error verifying admin session in background:', err);
      });

    return () => {
      mounted = false;
    };
  }, [user?.id, logout]);

  const login = useCallback(async (email, password) => {
    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return false;
    }
    setLoading(true);
    setError('');
    try {
      const snapshot = await db.collection('admins')
        .where('email', '==', email.trim())
        .where('password', '==', password)
        .limit(1)
        .get();

      if (snapshot.empty) {
        setError('Invalid email or password');
        setLoading(false);
        return false;
      }

      const adminDoc = snapshot.docs[0];
      const data = adminDoc.data();

      // Merge ALWAYS_VISIBLE keys so dashboard is always accessible
      const storedPermissions = Array.isArray(data.permissions) ? data.permissions : [];
      const permissions = Array.from(new Set([...ALWAYS_VISIBLE, ...storedPermissions]));

      const userData = {
        id: adminDoc.id,
        email: data.email,
        name: data.name,
        phone: data.phone || '',
        role: data.role || 'editor',
        permissions,
      };

      setUser(userData);
      try {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      } catch (e) {
        console.error('Failed to save admin user to localStorage:', e);
      }

      setLoading(false);
      return true;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, []);

  /**
   * Returns true if the logged-in admin has a given permission key.
   * - An 'admin' role has bypass access to everything.
   * - Only 'admin' role can access 'admin_users' permission.
   * - Otherwise, checks if the key is in the user's permissions array.
   */
  const hasPermission = useCallback(
    (key) => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      if (key === 'admin_users') return false; // Non-admins cannot access admin_users management
      if (!key) return true; // no restriction
      return user.permissions.includes(key);
    },
    [user]
  );

  const isLoggedIn = user !== null;

  return (
    <AuthContext.Provider
      value={{ user, loading, error, isLoggedIn, login, logout, setError, hasPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}